/**
 * The only HealthOS product events clients may send.
 *
 * Call sites address an event by its semantic key (`analyticsEvents.snapshotViewed`).
 * The wire shape lives in `analyticsEventCatalog`: the Segment event name plus the
 * `feature_type` / `activity_type` pair the data team groups on. Several HealthOS
 * events deliberately share one Segment name (`prompt_selected` carries eight of
 * them) and are told apart by `activity_type`, so the name is not a unique key and
 * must never be used as one.
 *
 * Properties are intentionally limited to operational context. Never add PHI,
 * user-entered text, media, member or signal record IDs, authentication
 * values, names, emails, or health measurements to this contract. Thrive CMS
 * catalog ids are allowed only where the catalog names them (`content_id` on
 * a Heartbeat recommendation tap).
 */
const ALL_SINKS = ["segment", "datadog"];
const DATADOG_ONLY = ["datadog"];
export const analyticsEventCatalog = {
    signedIn: {
        name: "signin_completed",
        featureType: "profile",
        activityType: "signin_completed",
    },
    onboardingStarted: {
        name: "onboarding_started",
        featureType: "onboarding",
        activityType: "onboarding_started",
    },
    onboardingStepViewed: {
        name: "onboarding_step_served",
        featureType: "onboarding",
        activityType: "onboarding_step_served",
        datadogOnlyProperties: ["lens"],
    },
    onboardingStepCompleted: {
        name: "onboarding_step_completed",
        featureType: "onboarding",
        activityType: "onboarding_step_completed",
        datadogOnlyProperties: ["lens"],
    },
    onboardingLensSelected: {
        name: "onboarding_lens_selected",
        featureType: "onboarding",
        activityType: "onboarding_lens_selected",
        datadogOnlyProperties: ["lens", "lens_changed"],
    },
    onboardingCompleted: {
        name: "onboarding_completed",
        featureType: "onboarding",
        activityType: "onboarding_completed",
        datadogOnlyProperties: ["lens"],
    },
    /*
     * Funnel instrumentation for the onboarding wizard. Everything below is
     * Datadog-only: these names are not in the data team's catalog, and the
     * questions they answer (which step loses people, who never gets in, who
     * comes back) are operational rather than warehouse questions.
     *
     * The pairing to hold onto when reading a funnel: `onboardingStepViewed` is
     * the denominator for a step and `onboardingStepCompleted` is its numerator.
     * Everything else explains the gap between them.
     */
    onboardingAccessDenied: {
        name: "onboarding_access_denied",
        featureType: "onboarding",
        activityType: "onboarding_access_denied",
        sinks: DATADOG_ONLY,
    },
    onboardingResumed: {
        name: "onboarding_resumed",
        featureType: "onboarding",
        activityType: "onboarding_resumed",
        sinks: DATADOG_ONLY,
    },
    onboardingStepSkipped: {
        name: "onboarding_step_skipped",
        featureType: "onboarding",
        activityType: "onboarding_step_skipped",
        sinks: DATADOG_ONLY,
    },
    onboardingStepBacked: {
        name: "onboarding_step_backed",
        featureType: "onboarding",
        activityType: "onboarding_step_backed",
        sinks: DATADOG_ONLY,
    },
    /* Fired on page hide while mid-wizard. The last one a session emits is where
     * that member actually dropped, which a step_viewed count alone cannot say. */
    onboardingAbandoned: {
        name: "onboarding_abandoned",
        featureType: "onboarding",
        activityType: "onboarding_abandoned",
        sinks: DATADOG_ONLY,
    },
    onboardingSubmitFailed: {
        name: "onboarding_submit_failed",
        featureType: "onboarding",
        activityType: "onboarding_submit_failed",
        sinks: DATADOG_ONLY,
    },
    onboardingExplainerBeatViewed: {
        name: "onboarding_explainer_beat_viewed",
        featureType: "onboarding",
        activityType: "onboarding_explainer_beat_viewed",
        sinks: DATADOG_ONLY,
    },
    onboardingExplainerSkipped: {
        name: "onboarding_explainer_skipped",
        featureType: "onboarding",
        activityType: "onboarding_explainer_skipped",
        sinks: DATADOG_ONLY,
    },
    onboardingExplainerCompleted: {
        name: "onboarding_explainer_completed",
        featureType: "onboarding",
        activityType: "onboarding_explainer_completed",
        sinks: DATADOG_ONLY,
    },
    onboardingAudioMuteToggled: {
        name: "onboarding_audio_mute_toggled",
        featureType: "onboarding",
        activityType: "onboarding_audio_mute_toggled",
        sinks: DATADOG_ONLY,
    },
    onboardingConsentToggled: {
        name: "onboarding_consent_toggled",
        featureType: "onboarding",
        activityType: "onboarding_consent_toggled",
        sinks: DATADOG_ONLY,
    },
    onboardingConsentFailed: {
        name: "onboarding_consent_failed",
        featureType: "onboarding",
        activityType: "onboarding_consent_failed",
        sinks: DATADOG_ONLY,
    },
    /*
     * One per member per local day, carrying how many days it has been since
     * they finished onboarding. Grouping this by `days_since_onboarding` is the
     * return curve: day 1, day 2, day 3, and where it falls off.
     *
     * The same number is also set as RUM global context for the session, so any
     * other action can be turned into a retention curve. "Came back" and "came
     * back and actually did something" are different questions, and only the
     * second one matters for a health product.
     */
    appDayOpened: {
        name: "app_day_opened",
        featureType: "profile",
        activityType: "app_day_opened",
        sinks: DATADOG_ONLY,
    },
    /*
     * Microsteps: the moment Heartbeat delivers, on whichever surface it lands.
     * The product calls these moments (ADR-0037); the analytics taxonomy keeps
     * the data team's microstep names so the history joins up. Completed and
     * deferred reuse the names their catalog already has. Shown goes to both
     * sinks too, so the warehouse has a served count to divide check-ins by; the
     * rest are new and stay Datadog-only until adopted.
     *
     * `microstep_shown` is the denominator. Every other event here explains
     * what the member did with a moment they saw.
     */
    microstepShown: {
        name: "microstep_shown",
        featureType: "microstep",
        activityType: "microstep_shown",
    },
    /* "Will do": the member took the moment on but has not done it yet. */
    microstepAccepted: {
        name: "microstep_accepted",
        featureType: "microstep",
        activityType: "microstep_accepted",
        sinks: DATADOG_ONLY,
    },
    /* "I did it", or a primary tap that is itself the doing. */
    microstepCompleted: {
        name: "microstep_checkin_completed",
        featureType: "microstep",
        activityType: "microstep_checkin_completed",
    },
    /* "Not now". */
    microstepDeferred: {
        name: "microstep_deferred",
        featureType: "microstep",
        activityType: "microstep_deferred",
    },
    /* Closed, swiped or scrimmed away without choosing. */
    microstepDismissed: {
        name: "microstep_dismissed",
        featureType: "microstep",
        activityType: "microstep_dismissed",
        sinks: DATADOG_ONLY,
    },
    /* "Why this?" or "See what this is based on". */
    microstepReasonOpened: {
        name: "microstep_reason_opened",
        featureType: "microstep",
        activityType: "microstep_reason_opened",
        sinks: DATADOG_ONLY,
    },
    /* A push notification carrying a moment was tapped. */
    microstepPushOpened: {
        name: "microstep_push_opened",
        featureType: "microstep",
        activityType: "microstep_push_opened",
        sinks: DATADOG_ONLY,
    },
    briefPlaybackStarted: {
        name: "content_started",
        featureType: "brief",
        activityType: "healthos_content_started",
        constantProperties: { content_type: "brief" },
    },
    briefAudioWaited: {
        name: "brief_audio_waited",
        featureType: "brief",
        activityType: "brief_audio_waited",
    },
    briefPlaybackStopped: {
        name: "content_stopped",
        featureType: "brief",
        activityType: "healthos_content_stopped",
        constantProperties: { content_type: "brief" },
    },
    briefPlaybackCompleted: {
        name: "content_completed",
        featureType: "brief",
        activityType: "healthos_content_completed",
        constantProperties: { content_type: "brief" },
    },
    onboardingAudioPlaybackStarted: {
        name: "content_started",
        featureType: "onboarding",
        activityType: "onboarding_audio_started",
        constantProperties: { content_type: "onboarding_audio" },
    },
    onboardingAudioPlaybackStopped: {
        name: "content_stopped",
        featureType: "onboarding",
        activityType: "onboarding_audio_stopped",
        constantProperties: { content_type: "onboarding_audio" },
    },
    onboardingAudioPlaybackCompleted: {
        name: "content_completed",
        featureType: "onboarding",
        activityType: "onboarding_audio_completed",
        constantProperties: { content_type: "onboarding_audio" },
    },
    contentOpened: {
        name: "content_selected",
        featureType: "content",
        activityType: "content_selected",
    },
    contentStarted: {
        name: "content_started",
        featureType: "content",
        activityType: "content_started",
    },
    /*
     * A Heartbeat recommendation was tapped. Fires alongside `contentOpened`,
     * which says what was opened; this says it was a recommendation and where
     * it sat. Shares `content_selected` with other opens; `activity_type` is
     * what tells it apart.
     */
    recommendedContentOpened: {
        name: "content_selected",
        featureType: "content",
        activityType: "recommended_content_selected",
        constantProperties: { recommendation_source: "heartbeat" },
        sinks: ALL_SINKS,
    },
    recommendedContentShown: {
        name: "recommended_content_shown",
        featureType: "content",
        activityType: "recommended_content_shown",
        sinks: DATADOG_ONLY,
    },
    contentShared: {
        name: "content_shared",
        featureType: "content",
        activityType: "content_shared",
    },
    resetStarted: {
        name: "content_started",
        featureType: "content",
        activityType: "reset_started",
        constantProperties: { content_type: "reset" },
    },
    resetCompleted: {
        name: "content_completed",
        featureType: "content",
        activityType: "reset_completed",
        constantProperties: { content_type: "reset" },
    },
    personalResetCreated: {
        name: "content_creation_completed",
        featureType: "content",
        activityType: "personal_reset_created",
    },
    personalResetShared: {
        name: "content_shared",
        featureType: "content",
        activityType: "personal_reset_shared",
    },
    personalResetDeleted: {
        name: "personal_reset_deleted",
        featureType: "content",
        activityType: "personal_reset_deleted",
    },
    /*
     * Tools, the Explore destination that groups Resets into quick practices.
     * The funnel is hub opened, tool opened, video viewed; `resetStarted` and
     * `resetCompleted` still carry the playback itself. Sent to Segment and
     * Datadog: every property is a closed set, a count or a boolean.
     */
    /*
     * Explore, the web hub of destination cards. Which card a member picked,
     * so the hub's pull can be read per destination. Datadog-only until the
     * data team adopts it.
     */
    exploreDestinationSelected: {
        name: "explore_destination_selected",
        featureType: "content",
        activityType: "explore_destination_selected",
        sinks: DATADOG_ONLY,
    },
    /** The Filters dialog opened on web Collection. Datadog only. */
    collectionFiltersOpened: {
        name: "collection_filters_opened",
        featureType: "content",
        activityType: "collection_filters_opened",
        sinks: DATADOG_ONLY,
    },
    /** A member applied or cleared Collection filters from the dialog. Datadog only. */
    collectionFiltersApplied: {
        name: "collection_filters_applied",
        featureType: "content",
        activityType: "collection_filters_applied",
        sinks: DATADOG_ONLY,
    },
    toolsOpened: {
        name: "tools_opened",
        featureType: "content",
        activityType: "tools_opened",
    },
    toolOpened: {
        name: "tool_opened",
        featureType: "content",
        activityType: "tool_opened",
    },
    toolVideoViewed: {
        name: "tool_video_viewed",
        featureType: "content",
        activityType: "tool_video_viewed",
    },
    toolAudioStarted: {
        name: "tool_audio_started",
        featureType: "content",
        activityType: "tool_audio_started",
    },
    /*
     * Courses, the Explore destination beside Tools: CMS courses of lessons of
     * videos, with saved progress. The funnel is hub opened, course opened,
     * video started, video completed, course completed. `course_id` is the
     * public Thrive CMS course id, a catalog id, never a member or record id.
     * Sent to Segment and Datadog like the Tools journey.
     */
    coursesOpened: {
        name: "courses_opened",
        featureType: "content",
        activityType: "courses_opened",
    },
    courseOpened: {
        name: "course_opened",
        featureType: "content",
        activityType: "course_opened",
    },
    courseVideoStarted: {
        name: "course_video_started",
        featureType: "content",
        activityType: "course_video_started",
    },
    courseVideoCompleted: {
        name: "course_video_completed",
        featureType: "content",
        activityType: "course_video_completed",
    },
    courseCompleted: {
        name: "course_completed",
        featureType: "content",
        activityType: "course_completed",
    },
    personalResetBuilderStepViewed: {
        name: "personal_reset_builder_step_viewed",
        featureType: "content",
        activityType: "personal_reset_builder_step_viewed",
    },
    connectionStarted: {
        name: "connection_started",
        featureType: "connection",
        activityType: "wearable_connection_started",
    },
    connectionCompleted: {
        name: "connection_completed",
        featureType: "connection",
        activityType: "wearable_connection_completed",
    },
    connectionRemoved: {
        name: "connection_removed",
        featureType: "connection",
        activityType: "wearable_connection_removed",
    },
    connectionSyncRequested: {
        name: "sync_requested",
        featureType: "connection",
        activityType: "wearable_sync_requested",
    },
    communityMomentCreated: {
        name: "community_moment_created",
        featureType: "community",
        activityType: "community_moment_created",
    },
    communityMomentRemoved: {
        name: "community_moment_removed",
        featureType: "community",
        activityType: "community_moment_removed",
    },
    communityReactionChanged: {
        name: "reaction_submitted",
        featureType: "community",
        activityType: "community_reaction_submitted",
    },
    communityKudosSent: {
        name: "kudos_submitted",
        featureType: "community",
        activityType: "community_kudos_submitted",
    },
    communityFilterSelected: {
        name: "item_filtered",
        featureType: "community",
        activityType: "community_filter_selected",
    },
    // Not in the data team's catalog. Named by the sheet's own conventions so the
    // community funnel stays legible; confirm with the data team before relying on it.
    communityItemReported: {
        name: "community_item_reported",
        featureType: "community",
        activityType: "community_item_reported",
    },
    communityYouViewed: {
        name: "community_you_viewed",
        featureType: "community",
        activityType: "community_you_viewed",
    },
    communityKudosRead: {
        name: "community_kudos_read",
        featureType: "community",
        activityType: "community_kudos_read",
    },
    communityKudosStarted: {
        name: "community_kudos_started",
        featureType: "community",
        activityType: "community_kudos_started",
    },
    /* A member's community profile, opened from a feed avatar, a route map
     * marker, a leaderboard row, the route roster, a walking group's member
     * list, or (their own) from the Settings profile card on mobile.
     * Datadog-only until the data team adopts it. */
    communityMemberProfileOpened: {
        name: "community_member_profile_opened",
        featureType: "community",
        activityType: "community_member_profile_opened",
        sinks: DATADOG_ONLY,
    },
    /*
     * The company walking challenge: the hub, joining, walking groups,
     * leaderboards, the route map and its timeline, and history. Sent to both
     * sinks, because challenge reporting is read from Segment. Nothing here
     * carries a challenge, group or member id, a group name, or a step or
     * distance count: the questions are about which surfaces get used, not how
     * far anybody walked.
     */
    challengeSectionViewed: {
        name: "challenge_section_viewed",
        featureType: "challenge",
        activityType: "challenge_section_viewed",
    },
    challengeJoined: {
        name: "challenge_joined",
        featureType: "challenge",
        activityType: "challenge_joined",
    },
    /* Closed the join ask without joining. Joining is consent to appear in the
     * company standings, so this is a real decision, not a missed tap. */
    challengeJoinDismissed: {
        name: "challenge_join_dismissed",
        featureType: "challenge",
        activityType: "challenge_join_dismissed",
    },
    challengeGroupCreated: {
        name: "challenge_group_created",
        featureType: "challenge",
        activityType: "challenge_group_created",
    },
    challengeGroupJoined: {
        name: "challenge_group_joined",
        featureType: "challenge",
        activityType: "challenge_group_joined",
    },
    challengeGroupLeft: {
        name: "challenge_group_left",
        featureType: "challenge",
        activityType: "challenge_group_left",
    },
    challengeGroupInviteSent: {
        name: "challenge_group_invite_sent",
        featureType: "challenge",
        activityType: "challenge_group_invite_sent",
    },
    challengeLeaderboardViewed: {
        name: "challenge_leaderboard_viewed",
        featureType: "challenge",
        activityType: "challenge_leaderboard_viewed",
    },
    challengeRouteOpened: {
        name: "challenge_route_opened",
        featureType: "challenge",
        activityType: "challenge_route_opened",
    },
    challengeRouteRosterOpened: {
        name: "challenge_route_roster_opened",
        featureType: "challenge",
        activityType: "challenge_route_roster_opened",
    },
    challengeTimelineOpened: {
        name: "challenge_timeline_opened",
        featureType: "challenge",
        activityType: "challenge_timeline_opened",
    },
    challengeTimelinePlayed: {
        name: "challenge_timeline_played",
        featureType: "challenge",
        activityType: "challenge_timeline_played",
    },
    challengeTimelinePaused: {
        name: "challenge_timeline_paused",
        featureType: "challenge",
        activityType: "challenge_timeline_paused",
    },
    challengeTimelineScrubbed: {
        name: "challenge_timeline_scrubbed",
        featureType: "challenge",
        activityType: "challenge_timeline_scrubbed",
    },
    challengeTimelineFinished: {
        name: "challenge_timeline_finished",
        featureType: "challenge",
        activityType: "challenge_timeline_finished",
    },
    challengeMapMarkerOpened: {
        name: "challenge_map_marker_opened",
        featureType: "challenge",
        activityType: "challenge_map_marker_opened",
    },
    challengeRouteVoted: {
        name: "challenge_route_voted",
        featureType: "challenge",
        activityType: "challenge_route_voted",
    },
    challengeStepGoalUpdated: {
        name: "challenge_step_goal_updated",
        featureType: "challenge",
        activityType: "challenge_step_goal_updated",
    },
    challengeRestDayTaken: {
        name: "challenge_rest_day_taken",
        featureType: "challenge",
        activityType: "challenge_rest_day_taken",
    },
    challengeRestDayWithdrawn: {
        name: "challenge_rest_day_withdrawn",
        featureType: "challenge",
        activityType: "challenge_rest_day_withdrawn",
    },
    challengeMilestoneShared: {
        name: "challenge_milestone_shared",
        featureType: "challenge",
        activityType: "challenge_milestone_shared",
    },
    challengeHistoryViewed: {
        name: "challenge_history_viewed",
        featureType: "challenge",
        activityType: "challenge_history_viewed",
    },
    challengePastChallengeViewed: {
        name: "challenge_past_challenge_viewed",
        featureType: "challenge",
        activityType: "challenge_past_challenge_viewed",
    },
    challengePastChallengeSectionViewed: {
        name: "challenge_past_challenge_section_viewed",
        featureType: "challenge",
        activityType: "challenge_past_challenge_section_viewed",
    },
    /* The once-per-member announcement that a challenge has just finished. */
    challengeWrapUpShown: {
        name: "challenge_wrap_up_shown",
        featureType: "challenge",
        activityType: "challenge_wrap_up_shown",
    },
    challengeWrapUpActed: {
        name: "challenge_wrap_up_acted",
        featureType: "challenge",
        activityType: "challenge_wrap_up_acted",
    },
    /*
     * The mobile wrap-up story that follows the announcement: one event per
     * screen reached, and the two-question survey at the end. Survey answers
     * are closed sets so nothing a member types can reach either sink.
     */
    challengeWrapUpStepViewed: {
        name: "challenge_wrap_up_step_viewed",
        featureType: "challenge",
        activityType: "challenge_wrap_up_step_viewed",
    },
    challengeWrapUpFeelingAnswered: {
        name: "challenge_wrap_up_feeling_answered",
        featureType: "challenge",
        activityType: "challenge_wrap_up_feeling_answered",
    },
    challengeWrapUpFeaturesAnswered: {
        name: "challenge_wrap_up_features_answered",
        featureType: "challenge",
        activityType: "challenge_wrap_up_features_answered",
    },
    challengeWrapUpSurveySkipped: {
        name: "challenge_wrap_up_survey_skipped",
        featureType: "challenge",
        activityType: "challenge_wrap_up_survey_skipped",
    },
    /*
     * Challenge awards, the celebrations that hand them out, and the challenge
     * notifications. These go to both sinks: they answer the "medals received"
     * and challenge notification rows of the data team's reporting, which is
     * read from Segment. Like the rest of the challenge events, nothing here
     * carries a challenge, group, award or member id, a name, or a step count.
     */
    challengeAwardsViewed: {
        name: "challenge_awards_viewed",
        featureType: "challenge",
        activityType: "challenge_awards_viewed",
    },
    challengeAwardsFiltered: {
        name: "challenge_awards_filtered",
        featureType: "challenge",
        activityType: "challenge_awards_filtered",
    },
    challengeAwardsRouteOpened: {
        name: "challenge_awards_route_opened",
        featureType: "challenge",
        activityType: "challenge_awards_route_opened",
    },
    challengeAwardViewed: {
        name: "challenge_award_viewed",
        featureType: "challenge",
        activityType: "challenge_award_viewed",
    },
    /* A waypoint or finish medal was handed out: the "medal received" moment. */
    challengeMilestoneCelebrationShown: {
        name: "challenge_milestone_celebration_shown",
        featureType: "challenge",
        activityType: "challenge_milestone_celebration_shown",
    },
    challengeMilestoneCelebrationActed: {
        name: "challenge_milestone_celebration_acted",
        featureType: "challenge",
        activityType: "challenge_milestone_celebration_acted",
    },
    challengeOvertakeShown: {
        name: "challenge_overtake_shown",
        featureType: "challenge",
        activityType: "challenge_overtake_shown",
    },
    challengeOvertakeActed: {
        name: "challenge_overtake_acted",
        featureType: "challenge",
        activityType: "challenge_overtake_acted",
    },
    challengeStreakCelebrationShown: {
        name: "challenge_streak_celebration_shown",
        featureType: "challenge",
        activityType: "challenge_streak_celebration_shown",
    },
    /*
     * A challenge notification (an overtake, a walking-group invitation) was
     * opened or dismissed. These fire alongside the generic notification events
     * where those already fire, and say which challenge notification it was.
     * Challenge reminder moments are microsteps, and report through
     * `microstepPushOpened` with their rule, so they are not repeated here.
     */
    challengeNotificationOpened: {
        name: "challenge_notification_opened",
        featureType: "challenge",
        activityType: "challenge_notification_opened",
    },
    challengeNotificationDismissed: {
        name: "challenge_notification_dismissed",
        featureType: "challenge",
        activityType: "challenge_notification_dismissed",
    },
    feedbackSubmitted: {
        name: "feedback_submitted",
        featureType: "profile",
        activityType: "healthos_feedback_submitted",
    },
    settingsUpdated: {
        name: "settings_updated",
        featureType: "profile",
        activityType: "healthos_settings_updated",
    },
    /* Which tab of the member's own profile page was shown. Datadog-only until
     * the data team adopts it. */
    profileTabViewed: {
        name: "profile_tab_viewed",
        featureType: "profile",
        activityType: "profile_tab_viewed",
        sinks: DATADOG_ONLY,
    },
    /* The member chose a profile photo action from the edit control on their
     * profile card. Datadog-only until the data team adopts it. */
    profilePhotoActionSelected: {
        name: "profile_photo_action_selected",
        featureType: "profile",
        activityType: "profile_photo_action_selected",
        sinks: DATADOG_ONLY,
    },
    /* The member copied one of their own account details (for a support
     * conversation, usually). Datadog-only until the data team adopts it. */
    profileDetailCopied: {
        name: "profile_detail_copied",
        featureType: "profile",
        activityType: "profile_detail_copied",
        sinks: DATADOG_ONLY,
    },
    manualEntryRecorded: {
        name: "activity_added",
        featureType: "activity",
        activityType: "healthos_activity_added_manual",
    },
    manualAddOpened: {
        name: "activity_add_opened",
        featureType: "activity",
        activityType: "healthos_activity_add_opened",
        sinks: DATADOG_ONLY,
    },
    manualAddGroupSelected: {
        name: "activity_add_group_selected",
        featureType: "activity",
        activityType: "healthos_activity_add_group_selected",
        sinks: DATADOG_ONLY,
    },
    manualEntryAbandoned: {
        name: "activity_add_abandoned",
        featureType: "activity",
        activityType: "healthos_activity_add_abandoned",
        sinks: DATADOG_ONLY,
    },
    manualEntryUpdated: {
        name: "activity_updated",
        featureType: "activity",
        activityType: "healthos_activity_updated",
    },
    manualEntryDeleted: {
        name: "activity_removed",
        featureType: "activity",
        activityType: "healthos_activity_removed",
    },
    reminderScheduled: {
        name: "reminder_scheduled",
        featureType: "content",
        activityType: "reminder_scheduled",
    },
    notificationOpened: {
        name: "prompt_selected",
        featureType: "notification",
        activityType: "notification_selected",
    },
    notificationDismissed: {
        name: "prompt_selected",
        featureType: "notification",
        activityType: "notification_dismissed",
    },
    experienceDismissed: {
        name: "prompt_selected",
        featureType: "notification",
        activityType: "experience_dismissed",
    },
    snapshotViewed: {
        name: "prompt_selected",
        featureType: "snapshot",
        activityType: "snapshot_selected",
    },
    snapshotMovementGoalUpdated: {
        name: "prompt_selected",
        featureType: "snapshot",
        activityType: "snapshot_movement_goal_updated",
    },
    snapshotGoalChanged: {
        name: "prompt_selected",
        featureType: "snapshot",
        activityType: "snapshot_goal_changed",
    },
    snapshotDaySelected: {
        name: "snapshot_day_selected",
        featureType: "snapshot",
        activityType: "snapshot_day_selected",
    },
    snapshotCalendarToggled: {
        name: "snapshot_calendar_toggled",
        featureType: "snapshot",
        activityType: "snapshot_calendar_toggled",
    },
    snapshotLogbookOpened: {
        name: "snapshot_logbook_opened",
        featureType: "snapshot",
        activityType: "snapshot_logbook_opened",
    },
    snapshotFoodCameraOpened: {
        name: "snapshot_food_camera_opened",
        featureType: "snapshot",
        activityType: "snapshot_food_camera_opened",
    },
    snapshotCalendarMonthPaged: {
        name: "snapshot_calendar_month_paged",
        featureType: "snapshot",
        activityType: "snapshot_calendar_month_paged",
    },
    snapshotDayCardOpened: {
        name: "snapshot_day_card_opened",
        featureType: "snapshot",
        activityType: "snapshot_day_card_opened",
    },
    logbookFiltersOpened: {
        name: "logbook_filters_opened",
        featureType: "snapshot",
        activityType: "logbook_filters_opened",
    },
    logbookFilterChanged: {
        name: "logbook_filter_changed",
        featureType: "snapshot",
        activityType: "logbook_filter_changed",
    },
    anatomyOpened: {
        name: "prompt_selected",
        featureType: "anatomy",
        activityType: "anatomy_selected",
    },
    companionPromptSelected: {
        name: "prompt_selected",
        featureType: "companion",
        activityType: "companion_prompt_selected",
    },
    // Chat with HealthOS opened from the add sheet. Carries the surface it was
    // opened from, never the conversation.
    assistantOpened: {
        name: "assistant_opened",
        featureType: "companion",
        activityType: "companion_assistant_opened",
        sinks: DATADOG_ONLY,
    },
    // Day Zero is the first-run tutorial. Not in the data team's catalog; filed
    // under onboarding by the sheet's own conventions.
    dayZeroStopShown: {
        name: "day_zero_stop_shown",
        featureType: "onboarding",
        activityType: "day_zero_stop_shown",
    },
    dayZeroStopActed: {
        name: "day_zero_stop_acted",
        featureType: "onboarding",
        activityType: "day_zero_stop_acted",
    },
    dayZeroComprehensionShown: {
        name: "day_zero_comprehension_shown",
        featureType: "onboarding",
        activityType: "day_zero_comprehension_shown",
    },
    dayZeroComprehensionAnswered: {
        name: "day_zero_comprehension_answered",
        featureType: "onboarding",
        activityType: "day_zero_comprehension_answered",
    },
    dayZeroSkipped: {
        name: "day_zero_skipped",
        featureType: "onboarding",
        activityType: "day_zero_skipped",
    },
    dayZeroCompleted: {
        name: "day_zero_completed",
        featureType: "onboarding",
        activityType: "day_zero_completed",
    },
    dayZeroCoachmarkShown: {
        name: "day_zero_coachmark_shown",
        featureType: "onboarding",
        activityType: "day_zero_coachmark_shown",
    },
    dayZeroCoachmarkActed: {
        name: "day_zero_coachmark_acted",
        featureType: "onboarding",
        activityType: "day_zero_coachmark_acted",
    },
    /* App-store review ask. The native rating sheet reports nothing back, so
     * these four events are the only view we have of the funnel: how often we
     * asked, what people said, whether the OS actually presented its sheet, and
     * who went to the listing from Settings instead. Datadog-only until the data
     * team adopts them. */
    reviewPromptShown: {
        name: "review_prompt_shown",
        featureType: "profile",
        activityType: "review_prompt_shown",
        sinks: DATADOG_ONLY,
    },
    reviewPromptAnswered: {
        name: "review_prompt_answered",
        featureType: "profile",
        activityType: "review_prompt_answered",
        sinks: DATADOG_ONLY,
    },
    reviewStoreSheetRequested: {
        name: "review_store_sheet_requested",
        featureType: "profile",
        activityType: "review_store_sheet_requested",
        sinks: DATADOG_ONLY,
    },
    reviewStoreListingOpened: {
        name: "review_store_listing_opened",
        featureType: "profile",
        activityType: "review_store_listing_opened",
        sinks: DATADOG_ONLY,
    },
    coachingSessionBooked: {
        name: "coaching_session_booked",
        featureType: "coaching",
        activityType: "coaching_session_booked",
        sinks: DATADOG_ONLY,
    },
    coachingSessionMoved: {
        name: "coaching_session_moved",
        featureType: "coaching",
        activityType: "coaching_session_moved",
        sinks: DATADOG_ONLY,
    },
    coachingSessionCancelled: {
        name: "coaching_session_cancelled",
        featureType: "coaching",
        activityType: "coaching_session_cancelled",
        sinks: DATADOG_ONLY,
    },
    coachingOutcomeRecorded: {
        name: "coaching_outcome_recorded",
        featureType: "coaching",
        activityType: "coaching_outcome_recorded",
        sinks: DATADOG_ONLY,
    },
    coachingMessageSent: {
        name: "coaching_message_sent",
        featureType: "coaching",
        activityType: "coaching_message_sent",
        sinks: DATADOG_ONLY,
    },
    coachingAssessmentSent: {
        name: "coaching_assessment_sent",
        featureType: "coaching",
        activityType: "coaching_assessment_sent",
        sinks: DATADOG_ONLY,
    },
    coachingAssessmentStarted: {
        name: "coaching_assessment_started",
        featureType: "coaching",
        activityType: "coaching_assessment_started",
        sinks: DATADOG_ONLY,
    },
    coachingAssessmentCompleted: {
        name: "coaching_assessment_completed",
        featureType: "coaching",
        activityType: "coaching_assessment_completed",
        sinks: DATADOG_ONLY,
    },
    coachingActionAgreed: {
        name: "coaching_action_agreed",
        featureType: "coaching",
        activityType: "coaching_action_agreed",
        sinks: DATADOG_ONLY,
    },
    coachingActionAnswered: {
        name: "coaching_action_answered",
        featureType: "coaching",
        activityType: "coaching_action_answered",
        sinks: DATADOG_ONLY,
    },
    coachingNoteAdded: {
        name: "coaching_note_added",
        featureType: "coaching",
        activityType: "coaching_note_added",
        sinks: DATADOG_ONLY,
    },
    coachingSharingChanged: {
        name: "coaching_sharing_changed",
        featureType: "coaching",
        activityType: "coaching_sharing_changed",
        sinks: DATADOG_ONLY,
    },
    coachingEnrollmentSaved: {
        name: "coaching_enrollment_saved",
        featureType: "coaching",
        activityType: "coaching_enrollment_saved",
        sinks: DATADOG_ONLY,
    },
    coachingAvailabilitySaved: {
        name: "coaching_availability_saved",
        featureType: "coaching",
        activityType: "coaching_availability_saved",
        sinks: DATADOG_ONLY,
    },
    coachingNoteEdited: {
        name: "coaching_note_edited",
        featureType: "coaching",
        activityType: "coaching_note_edited",
        sinks: DATADOG_ONLY,
    },
    coachingThreadExpanded: {
        name: "coaching_thread_expanded",
        featureType: "coaching",
        activityType: "coaching_thread_expanded",
        sinks: DATADOG_ONLY,
    },
    coachingWorkspaceViewed: {
        name: "coaching_workspace_viewed",
        featureType: "coaching",
        activityType: "coaching_workspace_viewed",
        sinks: DATADOG_ONLY,
    },
    coachingAttentionShown: {
        name: "coaching_attention_shown",
        featureType: "coaching",
        activityType: "coaching_attention_shown",
        sinks: DATADOG_ONLY,
    },
    coachingAttentionOpened: {
        name: "coaching_attention_opened",
        featureType: "coaching",
        activityType: "coaching_attention_opened",
        sinks: DATADOG_ONLY,
    },
    coachingPreReadViewed: {
        name: "coaching_pre_read_viewed",
        featureType: "coaching",
        activityType: "coaching_pre_read_viewed",
        sinks: DATADOG_ONLY,
    },
    coachingAttentionSetAside: {
        name: "coaching_attention_set_aside",
        featureType: "coaching",
        activityType: "coaching_attention_set_aside",
        sinks: DATADOG_ONLY,
    },
    coachingCoachBookingsSet: {
        name: "coaching_coach_bookings_set",
        featureType: "coaching",
        activityType: "coaching_coach_bookings_set",
        sinks: DATADOG_ONLY,
    },
    coachingEnrollmentEnded: {
        name: "coaching_enrollment_ended",
        featureType: "coaching",
        activityType: "coaching_enrollment_ended",
        sinks: DATADOG_ONLY,
    },
    coachingOpsExported: {
        name: "coaching_ops_exported",
        featureType: "coaching",
        activityType: "coaching_ops_exported",
        sinks: DATADOG_ONLY,
    },
    coachingCohortCreated: {
        name: "coaching_cohort_created",
        featureType: "coaching",
        activityType: "coaching_cohort_created",
        sinks: DATADOG_ONLY,
    },
    coachingCohortCoachChanged: {
        name: "coaching_cohort_coach_changed",
        featureType: "coaching",
        activityType: "coaching_cohort_coach_changed",
        sinks: DATADOG_ONLY,
    },
    coachingInvitesSent: {
        name: "coaching_invites_sent",
        featureType: "coaching",
        activityType: "coaching_invites_sent",
        sinks: DATADOG_ONLY,
    },
    coachingInviteResent: {
        name: "coaching_invite_resent",
        featureType: "coaching",
        activityType: "coaching_invite_resent",
        sinks: DATADOG_ONLY,
    },
    coachingInviteWithdrawn: {
        name: "coaching_invite_withdrawn",
        featureType: "coaching",
        activityType: "coaching_invite_withdrawn",
        sinks: DATADOG_ONLY,
    },
    coachingInviteJoined: {
        name: "coaching_invite_joined",
        featureType: "coaching",
        activityType: "coaching_invite_joined",
        sinks: DATADOG_ONLY,
    },
    coachingCoacheesSearched: {
        name: "coaching_coachees_searched",
        featureType: "coaching",
        activityType: "coaching_coachees_searched",
        sinks: DATADOG_ONLY,
    },
    coachingOpsAttentionOpened: {
        name: "coaching_ops_attention_opened",
        featureType: "coaching",
        activityType: "coaching_ops_attention_opened",
        sinks: DATADOG_ONLY,
    },
    coachingPhotoChanged: {
        name: "coaching_photo_changed",
        featureType: "coaching",
        activityType: "coaching_photo_changed",
        sinks: DATADOG_ONLY,
    },
    coachingTodayCardAnswered: {
        name: "coaching_today_card_answered",
        featureType: "coaching",
        activityType: "coaching_today_card_answered",
        sinks: DATADOG_ONLY,
    },
};
/**
 * Semantic keys, so a call site reads `analyticsEvents.snapshotViewed` rather
 * than repeating a wire name that several events share.
 */
export const analyticsEvents = Object.fromEntries(Object.keys(analyticsEventCatalog).map((key) => [key, key]));
export const analyticsScreens = [
    "today",
    "explore",
    "library",
    "community",
    "community_moments",
    "community_kudos",
    "connectors",
    "notifications",
    "settings",
    "onboarding",
    "snapshot",
    "challenges",
    "anatomy",
    "fingerprint",
    "callback",
    "signed_out",
    "operations",
    "unknown",
];
export function analyticsScreenForPath(pathname) {
    const path = pathname.replace(/^\/+/, "").toLowerCase();
    if (path === "" || path === "today" || path === "index")
        return "today";
    if (path.startsWith("library") || path.startsWith("explore/collection"))
        return "library";
    if (path === "explore")
        return "explore";
    if (path.startsWith("community/moments"))
        return "community_moments";
    if (path.startsWith("community/kudos"))
        return "community_kudos";
    if (path.startsWith("community"))
        return "community";
    if (path.startsWith("connectors") || path.startsWith("connections"))
        return "connectors";
    if (path.startsWith("notifications"))
        return "notifications";
    if (path.startsWith("settings"))
        return "settings";
    if (path.startsWith("onboarding"))
        return "onboarding";
    if (path.startsWith("snapshot"))
        return "snapshot";
    if (path.startsWith("challenges") || path.startsWith("steps"))
        return "challenges";
    if (path.startsWith("anatomy"))
        return "anatomy";
    if (path.startsWith("fingerprint") || path.startsWith("whma"))
        return "fingerprint";
    if (path.startsWith("terra-connect"))
        return "connectors";
    if (path.startsWith("callback") || path.startsWith("silent-callback"))
        return "callback";
    if (path.startsWith("signed-out"))
        return "signed_out";
    if (path.startsWith("operations"))
        return "operations";
    return "unknown";
}
/**
 * Resolve a semantic key and its call-site properties into the Segment payload:
 * the event name, plus `feature_type` / `activity_type` and any constants the
 * catalog stamps on. Both client adapters go through this, so the two surfaces
 * cannot drift on the taxonomy.
 */
export function resolveAnalyticsEvent(key, properties) {
    const spec = analyticsEventCatalog[key];
    const resolved = {
        ...("constantProperties" in spec ? spec.constantProperties : undefined),
        ...properties,
        feature_type: spec.featureType,
        activity_type: spec.activityType,
    };
    // Held back from the warehouse, not from the call site. A property the
    // Segment contract must not carry is still available to Datadog, so the
    // wizard does not need two events for one action.
    if ("datadogOnlyProperties" in spec) {
        for (const property of spec.datadogOnlyProperties)
            delete resolved[property];
    }
    return { name: spec.name, properties: resolved };
}
/**
 * The catalog key as the Datadog RUM action name.
 *
 * Segment names are deliberately shared (`prompt_selected` carries eight
 * events), which makes them useless as funnel steps: a step defined by
 * `prompt_selected` would match eight unrelated things. Catalog keys are unique
 * by construction, so deriving the action name from the key gives Datadog one
 * name per event for free, and it cannot drift from the semantic key a call
 * site already uses.
 */
export function datadogActionName(key) {
    return key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
}
/**
 * Resolve a semantic key into a Datadog RUM custom action: a unique action name
 * plus the same property bag Segment gets, so a question answered in one tool
 * is answerable in the other.
 */
export function resolveDatadogAction(key, properties) {
    const spec = analyticsEventCatalog[key];
    return {
        name: datadogActionName(key),
        context: {
            ...("constantProperties" in spec ? spec.constantProperties : undefined),
            ...properties,
            feature_type: spec.featureType,
            activity_type: spec.activityType,
        },
    };
}
/** Sinks an event is delivered to. Every event goes everywhere by default. */
export function analyticsSinksFor(key) {
    const spec = analyticsEventCatalog[key];
    return "sinks" in spec ? spec.sinks : ALL_SINKS;
}
export function analyticsEventGoesTo(key, sink) {
    return analyticsSinksFor(key).includes(sink);
}
/**
 * Coarse content_type for a Thrive CMS content kind, matching the existing
 * content catalog. Guide and Reset both land on `reset`.
 */
export function analyticsContentTypeForContentKind(kind) {
    if (kind === "Recipe")
        return "recipe";
    if (kind === "Article" || kind === "Story" || kind === "RoleModel")
        return "article";
    if (kind === "Guide" || kind === "Reset")
        return "reset";
    if (kind === "PodcastEpisode")
        return "resource";
    if (kind === "Meditation")
        return "meditation";
    if (kind === "Soundscape")
        return "soundscape";
    if (kind === "BedtimeStory")
        return "bedtime_story";
    return "unknown";
}
/**
 * Normalizes client and API provider labels into a closed, PHI-safe taxonomy.
 */
export function analyticsConnectionProviderFor(provider) {
    const normalized = provider?.trim().toLowerCase();
    if (!normalized)
        return "unknown";
    const aliases = {
        "apple-health": "apple_health",
        apple_health: "apple_health",
        "calendar-google": "google_calendar",
        google_calendar: "google_calendar",
        "calendar-microsoft": "outlook_calendar",
        outlook_calendar: "outlook_calendar",
        google_fit: "google",
        "health-connect": "health_connect",
        health_connect: "health_connect",
        "google-health": "google_health",
        google_health: "google_health",
    };
    if (aliases[normalized])
        return aliases[normalized];
    const providers = [
        "google",
        "fitbit",
        "oura",
        "garmin",
        "whoop",
        "polar",
        "peloton",
        "ultrahuman",
        "strava",
        "myfitnesspal",
        "flow",
        "withings",
        "dexcom",
        "terra",
    ];
    return providers.includes(normalized)
        ? normalized
        : "unknown";
}
/**
 * Read a notification payload as a challenge notification, or null when it is
 * not one. Takes only the kind and the overtake role: the payload also carries
 * the other member's name and avatar, which must never reach analytics.
 */
export function challengeNotificationAnalyticsFor(data) {
    if (!data || typeof data !== "object")
        return null;
    const { kind, role } = data;
    if (kind === "challenge.group_invitation")
        return { notification_kind: "group_invitation" };
    if (kind === "challenge.wrap_up_ready")
        return { notification_kind: "wrap_up" };
    if (kind !== "challenge.overtake")
        return null;
    return role === "overtaker" || role === "overtaken"
        ? { notification_kind: "overtake", overtake_role: role }
        : { notification_kind: "overtake" };
}
/**
 * Report a challenge notification being opened or dismissed, by kind, through
 * the client's own `track`. A notification that is not a challenge one reports
 * nothing; the generic notification events still cover it.
 */
export function trackChallengeNotification(track, action, data, surface) {
    const challenge = challengeNotificationAnalyticsFor(data);
    if (!challenge)
        return;
    if (action === "opened") {
        track(analyticsEvents.challengeNotificationOpened, {
            ...challenge,
            notification_surface: surface,
        });
        return;
    }
    // A push has no dismiss we can see: the OS clears it without telling us.
    if (surface === "push")
        return;
    track(analyticsEvents.challengeNotificationDismissed, {
        notification_kind: challenge.notification_kind,
        notification_surface: surface,
    });
}
/**
 * Convert the API's safe feedback subject type into the coarser product
 * analytics taxonomy. This deliberately accepts only the type, never the
 * feedback's free text, identifier, or metadata.
 */
export function feedbackAnalyticsSubjectForType(subjectType) {
    if (subjectType === "attention_card" || subjectType === "screen")
        return "experience";
    if (subjectType === "article" ||
        subjectType === "story" ||
        subjectType === "recipe" ||
        subjectType === "reset" ||
        subjectType === "brief" ||
        subjectType === "reminder" ||
        subjectType === "library_item") {
        return "content";
    }
    return "unknown";
}
export function createNoopAnalyticsClient() {
    return {
        identify: () => { },
        reset: () => { },
        page: () => { },
        track: () => { },
    };
}
//# sourceMappingURL=events.js.map